import { useRequestMedicalContext } from "../Contexts/RequestMedicalTestContext";
import { Checkbox, Divider, IconButton, InputBase, Paper } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PropTypes from "prop-types";
import {
  CheckBoxOutlineBlankOutlined,
  CheckBoxOutlined,
} from "@mui/icons-material";

const ProcedureSection = ({ title, procedures }) => {
  const { setSelectedProcedures, selectedProcedures } =
    useRequestMedicalContext();

  const isChecked = (procedureName, itemName, itemClass) => {
    const procedures = selectedProcedures.filter(
      (p) =>
        p.name.substring(p.name.indexOf(":") + 1) ===
        procedureName.substring(procedureName.indexOf(":") + 1)
    );

    for (const procedure of procedures) {
      if (
        procedure.coveredProcedures.some(
          (p) => p.cptCode === itemName && p.class === itemClass
        ) ||
        procedure.notCoveredProcedures.some(
          (p) => p.cptCode === itemName && p.class === itemClass
        )
      ) {
        return true;
      }
    }
    return false;
  };

  const handleCheckboxChange = (e, procedureName, item) => {
    setSelectedProcedures((prevState) => {
      let procedureFound = false;
      const updatedProcedures = prevState.map((procedure) => {
        if (
          procedure.name.substring(procedure.name.indexOf(":") + 1) ===
          procedureName.substring(procedureName.indexOf(":") + 1)
        ) {
          procedureFound = true;
          let coveredProcedures = [...procedure.coveredProcedures];

          if (e.target.checked) {
            coveredProcedures.push(item);
          } else {
            coveredProcedures = coveredProcedures.filter(
              (p) => p.name !== item.name
            );
          }

          if (coveredProcedures.length === 0) {
            return null;
          } else {
            return {
              ...procedure,
              coveredProcedures,
            };
          }
        }
        return procedure;
      });

      if (!procedureFound && e.target.checked) {
        updatedProcedures.push({
          name: procedureName,
          coveredProcedures: [item],
          notCoveredProcedures: [],
        });
      }

      return updatedProcedures.filter(Boolean);
    });
  };

  return (
    <>
      {procedures.length > 0 && (
        <>
          <Divider
            textAlign="left"
            sx={{
              "& .MuiDivider-wrapper": {
                padding: 0,
                paddingRight: 2,
              },
              "&::before": { width: 0 },
            }}
          >
            <p className="text-[#60656D]">
              {title.substring(title.indexOf(":") + 1)}
            </p>
          </Divider>
          {procedures.map((procedure) => {
            const isLabProcedure = procedure.class === "LABORATORY PROCEDURES";
            return (
              <div key={procedure.cptCode} className="flex flex-row my-2">
                <label id="labl" className="w-full mt-1">
                  <input
                    name="procedures"
                    type="checkbox"
                    onChange={(e) => handleCheckboxChange(e, title, procedure)}
                    disabled={!isLabProcedure}
                    checked={isChecked(
                      title,
                      procedure.cptCode,
                      procedure.class
                    )}
                  />
                  <div
                    className={`flex w-full text-sm p-3 border rounded-lg ${
                      isLabProcedure ? "cursor-pointer" : "cursor-default"
                    }`}
                  >
                    <div>
                      <Checkbox
                        disabled={!isLabProcedure}
                        checked={isChecked(
                          title,
                          procedure.cptCode,
                          procedure.class
                        )}
                        icon={<CheckBoxOutlineBlankOutlined />}
                        checkedIcon={<CheckBoxOutlined />}
                        sx={{
                          padding: 0,
                          "&.Mui-disabled": {
                            color: "#0947AB",
                          },
                          "&.Mui-checked": {
                            color: "#0947AB",
                          },
                        }}
                        onChange={(e) =>
                          handleCheckboxChange(e, title, procedure)
                        }
                      />
                    </div>
                    <div className="pl-1">
                      <p className="ml-2">{procedure.name}</p>
                    </div>
                  </div>
                </label>
              </div>
            );
          })}
        </>
      )}
    </>
  );
};
ProcedureSection.propTypes = {
  title: PropTypes.string,
  procedures: PropTypes.any,
};

const ProceduresList = ({ proceduresData }) => (
  <>
    {proceduresData.some((data) => data.coveredProcedures.length > 0) && (
      <>
        <p>Covered</p>
        {proceduresData.map((data, index) => (
          <ProcedureSection
            key={data}
            title={data.name}
            procedures={data.coveredProcedures}
          />
        ))}
      </>
    )}

    {proceduresData.some((data) => data.notCoveredProcedures.length > 0) && (
      <>
        <p>Not Available</p>
        {proceduresData.map((data, index) => (
          <ProcedureSection
            key={data}
            title={data.name}
            procedures={data.notCoveredProcedures}
          />
        ))}
      </>
    )}
  </>
);

ProceduresList.propTypes = {
  proceduresData: PropTypes.any,
};

const RenderProcedures = () => {
  const {
    searchProcedures,
    searchProceduresResults,
    mappedProcedures,
    setSearchProcedures,
    setSearchProceduresResults,
  } = useRequestMedicalContext();
  const proceduresData =
    searchProcedures !== "" ? searchProceduresResults : mappedProcedures;
  const containsProcedure = (procedures, value) => {
    return procedures.some((procedure) =>
      procedure.name.toUpperCase().includes(value.toUpperCase())
    );
  };

  const handleSearch = (e) => {
    const searchValue = e.target.value.toUpperCase();

    setSearchProcedures(searchValue);

    const results = mappedProcedures.filter(
      (data) =>
        containsProcedure(data.coveredProcedures, searchValue) ||
        containsProcedure(data.notCoveredProcedures, searchValue)
    );

    setSearchProceduresResults(results);
  };
  return (
    proceduresData && (
      <>
        <Paper
          component="form"
          sx={{
            width: "100%",
            mt: 2,
            mb: 2,
            display: "flex",
            alignItems: "center",
          }}
        >
          <IconButton disabled aria-label="search">
            <SearchIcon />
          </IconButton>
          <InputBase
            sx={{ ml: 1, flex: 1 }}
            placeholder="Search procedures"
            onChange={handleSearch}
            inputProps={{ "aria-label": "search procedures" }}
          />
        </Paper>
        <ProceduresList proceduresData={proceduresData} />
      </>
    )
  );
};

export default RenderProcedures;
